import {
    getGridDateOperators,
    getGridNumericOperators,
    getGridSingleSelectOperators,
    getGridStringOperators,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
    ValueOptions,
} from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import React from 'react';
import formatDateWithBrowserLocale from '../../../core/legacy/utils/date';
import { GlobalStateTag, TagVariant } from '../../network/list-network/GlobalStateTag';
import { getGlobalStateTagVariant } from '../../network/list-network/utils';
import { ValueTypeToFilter } from '../../session/list-sessions/getDataGridColumns';
import CustomSingleSelectEdit from './CustomSingleSelectEdit';
import CustomTextAreaEdit from './CustomTextAreaEdit';
import ProjectStringDisplay from './ProjectStringDisplay';
import SingleSelectCell from './SingleSelectCell';

const EMPTY_VALUE = '';

const getFilter = (type: ValueTypeToFilter) => {
    switch (type) {
        case 'string':
            return getGridStringOperators().filter(({ value }) => value !== 'isAnyOf');
        case 'direction':
            return getGridSingleSelectOperators().filter(({ value }) => value === 'is');
        case 'number':
            return getGridNumericOperators().filter(({ value }) => ['>', '<'].includes(value));
        default:
            return getGridDateOperators().filter(({ value }) => ['onOrAfter', 'before', 'is', 'not'].includes(value));
    }
};

export const getDataGridColumns = (
    t: TFunction,
    sortable: boolean,
    repairSolutions?: Array<{ name: string; cost: number }>,
): GridColDef[] => {
    const repairSolutionCosts: Record<string, number> = (repairSolutions || []).reduce((acc, solution) => {
        acc[solution.name] = solution.cost;

        return acc;
    }, {} as Record<string, number>);
    const baseColDef = (align: GridColDef['align'] = 'right'): Partial<GridColDef> => ({
        align,
        headerAlign: 'center',
        cellClassName: 'light',
        sortable,
        filterable: true,
        hideable: true,
    });

    return [
        { ...GRID_CHECKBOX_SELECTION_COL_DEF, disableReorder: true, hideable: false },
        {
            ...baseColDef('left'),
            cellClassName: 'dark',
            display: 'flex',
            field: 'roadName',
            filterOperators: getFilter('string'),
            flex: 3,
            headerName: t('reference.roadSection.roadName'),
            minWidth: 100,
            renderCell: ({ row }) => ProjectStringDisplay({ data: row.roadName }),
        },
        {
            ...baseColDef(),
            field: 'name',
            headerName: t('reference.roadSection.name'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.name || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'prd',
            headerName: t('reference.roadSection.prd'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.prd || '',
        },
        {
            ...baseColDef(),
            field: 'abd',
            headerName: t('reference.roadSection.abd'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.abd ?? null,
        },
        {
            ...baseColDef(),
            field: 'prf',
            headerName: t('reference.roadSection.prf'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.prf || '',
        },
        {
            ...baseColDef(),
            field: 'abf',
            headerName: t('reference.roadSection.abf'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.abf || null,
        },
        {
            ...baseColDef(),
            field: 'gestionary',
            headerName: t('reference.roadSection.gestionary'),
            minWidth: 150,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.gestionary || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'cumulStart',
            headerName: t('reference.roadSection.start'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => `${row.cumulStart.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'cumulEnd',
            headerName: t('reference.roadSection.end'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => `${row.cumulEnd.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'category',
            headerName: t('reference.roadSection.category'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.category || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'trucks',
            headerName: t('reference.roadSection.trucks'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.trucks || '',
        },
        {
            ...baseColDef(),
            field: 'urban',
            headerName: t('reference.roadSection.urban'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.urban || '',
        },
        {
            ...baseColDef(),
            field: 'roadType',
            headerName: t('reference.roadSection.roadType'),
            minWidth: 150,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.roadType || '',
        },
        {
            ...baseColDef(),
            field: 'year',
            headerName: t('reference.roadSection.year'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.year || '',
        },
        {
            ...baseColDef(),
            field: 'overallRate',
            headerName: t('networkList.networkCondition.overallRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.overallRate || '-',
        },
        {
            ...baseColDef(),
            field: 'overallState',
            headerName: t('networkList.networkCondition.overallState'),
            minWidth: 100,
            flex: 2,
            type: 'singleSelect',
            valueOptions: ['good', 'averageGood', 'averageBad', 'bad', 'unknown'],
            getOptionLabel: (value: ValueOptions) => {
                const variantTitles: Record<TagVariant, string> = {
                    good: 'labels.good',
                    averageGood: 'labels.averageGood',
                    averageBad: 'labels.averageBad',
                    bad: 'labels.bad',
                    unknown: 'labels.notScanned',
                };

                return t(variantTitles[value as TagVariant] || String(value));
            },
            filterOperators: getGridSingleSelectOperators(),
            valueGetter: (_, row) => {
                const variant: TagVariant = getGlobalStateTagVariant(row);

                return variant;
            },
            renderCell: ({ row }) => {
                const variant: TagVariant = getGlobalStateTagVariant(row);

                return GlobalStateTag({ variant });
            },
        },
        {
            ...baseColDef(),
            field: 'lastScanDate',
            headerName: t('networkList.networkCondition.lastScanDate'),
            minWidth: 150,
            flex: 2,
            type: 'date',
            filterOperators: getFilter('date'),
            valueGetter: (_, row) => (row.lastScanDate ? new Date(row.lastScanDate) : null),
            renderCell: ({ value }) => (value ? formatDateWithBrowserLocale(new Date(value), 'MM/yyyy') : EMPTY_VALUE),
        },
        {
            ...baseColDef(),
            field: 'pourcentageScanned',
            headerName: t('networkList.networkCondition.pourcentageScanned'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.pourcentageScanned || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'surfaceRate',
            headerName: t('networkList.networkCondition.surfaceRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.surfaceRate || '-',
        },
        {
            ...baseColDef(),
            field: 'structureRate',
            headerName: t('networkList.networkCondition.structureRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.structureRate || '-',
        },
        {
            ...baseColDef(),
            field: 'length',
            headerName: t('reference.roadSection.length'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => Math.abs(row.cumulEnd - row.cumulStart),
            renderCell: ({ value }) => `${value.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'width',
            headerName: t('reference.roadSection.width'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) =>
                row.width?.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) ??
                EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'surface',
            headerName: t('networkList.networkCondition.surface'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => {
                if (row.width == null) {
                    return null;
                }

                return Math.abs((row.cumulEnd - row.cumulStart) * row.width);
            },
            renderCell: ({ value }) => (value != null && !Number.isNaN(value) ? `${value.toFixed(0)}` : EMPTY_VALUE),
        },
        {
            ...baseColDef(),
            field: 'priority',
            headerName: t('projectList.projectAttributes.priority'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'singleSelect',
            valueOptions: ['P1', 'P2', 'P3'],
            filterOperators: getGridSingleSelectOperators(),
            valueGetter: (_, row) => row.priority ?? null,
            valueSetter: (value, row) => ({ ...row, priority: value === EMPTY_VALUE ? null : value }),
            renderEditCell: (params) =>
                React.createElement(CustomSingleSelectEdit, { ...params, valueOptions: ['P1', 'P2', 'P3'] }),
            renderCell: ({ row }) => SingleSelectCell({ value: row.priority || EMPTY_VALUE }),
        },
        {
            ...baseColDef(),
            field: 'repairSolution',
            headerName: t('projectList.projectAttributes.repairSolution'),
            minWidth: 250,
            flex: 2,
            editable: true,
            type: 'singleSelect',
            valueOptions: [EMPTY_VALUE, ...Object.keys(repairSolutionCosts)],
            filterOperators: getGridSingleSelectOperators(),
            valueGetter: (_, row) => row.repairSolution ?? null,
            valueSetter: (value, row) => {
                const normalizedValue = value === EMPTY_VALUE ? null : value;
                const conditionalCost = normalizedValue ? repairSolutionCosts[normalizedValue] || null : null;

                return { ...row, repairSolution: normalizedValue, conditionalCost };
            },
            renderEditCell: (params) =>
                React.createElement(CustomSingleSelectEdit, {
                    ...params,
                    valueOptions: [EMPTY_VALUE, ...Object.keys(repairSolutionCosts)],
                }),
            renderCell: ({ row }) => SingleSelectCell({ value: row.repairSolution || EMPTY_VALUE }),
        },
        {
            ...baseColDef(),
            field: 'conditionalCost',
            headerName: t('projectList.projectAttributes.conditionalCost'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => (row.conditionalCost != null ? row.conditionalCost : EMPTY_VALUE),
        },
        {
            ...baseColDef(),
            field: 'totalCost',
            headerName: t('projectList.projectAttributes.totalCost'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => {
                const surface = row.width != null ? Math.abs((row.cumulEnd - row.cumulStart) * row.width) : null;

                if (surface == null || row.conditionalCost == null) {
                    return null;
                }

                return surface * row.conditionalCost;
            },
            renderCell: ({ value }) =>
                value != null && !Number.isNaN(value)
                    ? value.toLocaleString('fr-FR', { maximumFractionDigits: 0 })
                    : EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            editable: true,
            field: 'repairYear',
            headerName: t('projectList.projectAttributes.repairYear'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => row.repairYear ?? null,
            valueSetter: (value, row) => {
                // Normalize: empty string or falsy becomes null, numeric string becomes number
                let normalizedValue: number | null = null;

                if (value === '' || value === null || value === undefined) {
                    normalizedValue = null;
                } else if (typeof value === 'string') {
                    const trimmed = value.trim();
                    if (trimmed === '') {
                        normalizedValue = null;
                    } else {
                        const numValue = Number(trimmed);
                        normalizedValue = Number.isNaN(numValue) ? null : numValue;
                    }
                } else if (typeof value === 'number') {
                    normalizedValue = value;
                }

                return { ...row, repairYear: normalizedValue };
            },
            renderCell: ({ row }) => row.repairYear ?? EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'repairFollowUp',
            headerName: t('projectList.projectAttributes.repairFollowUp'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'singleSelect',
            valueOptions: ['budgeté', 'commandé', 'en cours', 'à récéptionner'],
            filterOperators: getGridSingleSelectOperators(),
            valueGetter: (_, row) => row.repairFollowUp ?? null,
            valueSetter: (value, row) => ({ ...row, repairFollowUp: value === EMPTY_VALUE ? null : value }),
            renderEditCell: (params) =>
                React.createElement(CustomSingleSelectEdit, {
                    ...params,
                    valueOptions: ['budgeté', 'commandé', 'en cours', 'à récéptionner'],
                }),
            renderCell: ({ row }) => SingleSelectCell({ value: row.repairFollowUp || EMPTY_VALUE }),
        },
        {
            ...baseColDef(),
            field: 'assignee',
            headerName: t('projectList.projectAttributes.assignee'),
            minWidth: 100,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.assignee ?? null,
            valueSetter: (value, row) => ({ ...row, assignee: value === EMPTY_VALUE ? null : value }),
            renderCell: ({ row }) => row.assignee || EMPTY_VALUE,
        },
        {
            ...baseColDef('left'),
            field: 'comment',
            headerName: t('projectList.projectAttributes.comment'),
            minWidth: 250,
            flex: 2,
            editable: true,
            type: 'string',
            valueGetter: (_, row) => row.comment ?? null,
            valueSetter: (value, row) => ({ ...row, comment: value === EMPTY_VALUE ? null : value }),
            renderEditCell: (params) => CustomTextAreaEdit(params),
            renderCell: ({ row }) => row.comment || EMPTY_VALUE,
        },
    ];
};
